import { motion } from "framer-motion";
import { Technology } from "@/components/nyla/Technology";
import { PurityProcess } from "@/components/nyla/PurityProcess";
import { Stats } from "@/components/nyla/Stats";
import { CtaBanner } from "@/components/nyla/CtaBanner";
import { SEO } from "@/components/nyla/SEO";

const innovationJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://nylaairwater.earth/" },
    { "@type": "ListItem", position: 2, name: "Innovation", item: "https://nylaairwater.earth/innovation" },
  ],
};

const principles = [
  {
    title: "We read the sky.",
    desc: "Humidity, temperature, and air quality tell us when the atmosphere is at its purest — so every harvest begins at exactly the right moment, and not a moment sooner.",
  },
  {
    title: "Nothing is left to chance.",
    desc: "Through every season, purification and mineral enrichment are tuned gently and continuously. If humidity shifts, we respond. If mineral ratios drift, they are corrected. Perfection is not a goal — it is the baseline.",
  },
  {
    title: "A facility built on care.",
    desc: "Every valve, filter, and seal is maintained ahead of time, so production never cuts a corner. Each bottle is checked for fill, seal, mineral balance, and pH before it leaves our facility.",
  },
];

export default function Innovation() {
  return (
    <div data-testid="innovation-page" className="pt-14">
      <SEO
        title="Our Craft & Innovation"
        description="From air to water — the five-stage purification craft behind Nyla. Atmospheric harvesting, layered purification, 0.2μm filtration, precise mineralisation, and elegant bottling."
        path="/innovation"
        keywords="atmospheric water generation, water purification process, 0.2 micron filtration, precise mineralisation, alkaline pH water process, sustainable water craft"
        jsonLd={innovationJsonLd}
      />
      <Technology />

      <section data-testid="principles-section" className="py-24 md:py-32 bg-black text-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16 md:mb-24 max-w-3xl mx-auto"
          >
            <p className="text-xs md:text-sm tracking-[0.2em] uppercase font-semibold text-[#86868B] mb-6">Our Principles</p>
            <h2 data-testid="principles-heading" className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight font-bold">
              We don't just make water. We craft certainty.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {principles.map((p, i) => (
              <motion.div
                key={p.title}
                data-testid={`principle-card-${i}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: i * 0.12 }}
                className="bg-[#0A0A0B] border border-white/10 rounded-3xl p-8 lg:p-10"
              >
                <span className="font-display text-4xl font-bold text-white/20">{`0${i + 1}`}</span>
                <h3 className="font-display text-xl md:text-2xl tracking-tight font-bold mt-6 mb-3">{p.title}</h3>
                <p className="text-base leading-relaxed text-[#86868B]">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Stats />
      <PurityProcess />
      <CtaBanner />
    </div>
  );
}
